import { LuIcon } from "@overmap-ai/blocks"
import { EMPTY_ARRAY, type IssueTypePriority, type Stored } from "@overmap-ai/common"
import { use, useCallback } from "react"

import { IssuePriorityMultiSelect } from "../../../components/DropdownMultiSelects/IssuePriorityMultiSelect"
import { IssuePrioritySelect } from "../../../components/DropdownSelects/IssuePrioritySelect"
import { IssuePriorityIcon, IssuePriorityIcons } from "../../../components/Icons/IssuePriorityIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { IssuePriorityFilterDef } from "./IssuePriorityFilterDef"

type IssuePriorityFilterCellProps = FilterDefRenderProps<IssuePriorityFilterDef>

export const IssuePriorityFilterCell = (props: IssuePriorityFilterCellProps) => {
	const { filter, filterDef, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()
	const issueTypePriorities = filterDef.issueTypePriorities

	const handleValueSelect = useCallback(
		(value?: Stored<IssueTypePriority>) => {
			filter.setFilterValue(value?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(values: Stored<IssueTypePriority>[]) => {
			if (values.length > 0) {
				filter.setFilterValue(values.map(({ uuid }) => uuid))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined
				const selectedIssueTypePriority = issueTypePriorities.find(
					({ uuid }) => uuid === equalsModifierFilterValue,
				)

				return (
					<IssuePrioritySelect
						size={size}
						required={false}
						value={selectedIssueTypePriority}
						onValueChange={handleValueSelect}
						priorities={issueTypePriorities}
					>
						<FilterCellButton className="truncate">
							{selectedIssueTypePriority !== undefined ? (
								<>
									<IssuePriorityIcon priority={selectedIssueTypePriority.uuid} />
									{selectedIssueTypePriority.name}
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</IssuePrioritySelect>
				)
			}
			case "includes":
			case "excludes": {
				const includesModifier = filterDef.modifiers.includes
				const includesModifierFilterValue =
					filterValue !== undefined && includesModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined
				const selectedIssueTypePriorities = issueTypePriorities.filter(({ uuid }) =>
					(includesModifierFilterValue ?? EMPTY_ARRAY).includes(uuid),
				)

				const getLabelUi = () => {
					switch (selectedIssueTypePriorities.length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<IssuePriorityIcon priority={selectedIssueTypePriorities[0].uuid} />
									{selectedIssueTypePriorities[0].name}
								</>
							)
						default:
							return (
								<>
									<IssuePriorityIcons
										priorities={selectedIssueTypePriorities.map(({ uuid }) => uuid)}
									/>
									{`${selectedIssueTypePriorities.length} priorities`}
								</>
							)
					}
				}

				return (
					<IssuePriorityMultiSelect
						size={size}
						values={selectedIssueTypePriorities}
						onValuesChange={handleValuesChange}
						priorities={issueTypePriorities}
					>
						<FilterCellButton className="truncate">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</IssuePriorityMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.priority} />
				Priority
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
